import React from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

const BLANK_REF = {
  authors: "",
  year: "",
  title: "",
  source: "",
  url: "",
  refType: "journal",
};

function formatAPA({ authors, year, title, source, url }) {
  let parts = [];
  if (authors) parts.push(authors);
  if (year) parts.push(`(${year}).`);
  if (title) parts.push(`${title}.`);
  if (source) parts.push(`${source}.`);
  if (url) parts.push(url);
  return parts.join(" ");
}

function formatInlineCitation({ authors, year }) {
  const lastName = authors ? authors.split(",")[0].trim() : "Author";
  const yr = year || "n.d.";
  return `(${lastName}, ${yr})`;
}

export default function ReferencesPanel({ references, onChange, onInsertCitation }) {
  const [form, setForm] = React.useState(BLANK_REF);
  const [showForm, setShowForm] = React.useState(false);

  const handleAdd = () => {
    if (!form.title && !form.authors) return;
    const updated = [...references, { ...form, id: Date.now().toString() }];
    onChange(updated);
    setForm(BLANK_REF);
    setShowForm(false);
  };

  const handleRemove = (id) => {
    onChange(references.filter((r) => r.id !== id));
  };

  const handleCopy = (ref) => {
    navigator.clipboard.writeText(formatAPA(ref));
  };

  const REF_TYPE_LABELS = {
    journal: "Journal",
    book: "Book",
    website: "Website",
    conference: "Conference",
  };

  return (
    <Box
      sx={{
        width: 290,
        minHeight: "100vh",
        borderLeft: "1px solid #e0e0e0",
        backgroundColor: "#fafafa",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", backgroundColor: "white" }}>
        <Typography variant="subtitle1" fontWeight="bold">
          References
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {references.length} source{references.length !== 1 ? "s" : ""} · APA format
        </Typography>
      </Box>

      {/* List */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
        {references.length === 0 && !showForm && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 2, textAlign: "center", lineHeight: 1.6 }}
          >
            No sources yet. Add a reference to get started.
          </Typography>
        )}

        {references.map((ref, idx) => (
          <Box
            key={ref.id}
            sx={{
              p: 1.5,
              mb: 1,
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 0.5, textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              [{idx + 1}] {REF_TYPE_LABELS[ref.refType] ?? ref.refType}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.75rem", color: "#333", lineHeight: 1.5 }}
            >
              {formatAPA(ref) || <em>Incomplete reference</em>}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5, gap: 0.5 }}>
              <Tooltip title="Insert inline citation">
                <IconButton size="small" onClick={() => onInsertCitation(formatInlineCitation(ref))}>
                  <FormatQuoteIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy full citation (APA)">
                <IconButton size="small" onClick={() => handleCopy(ref)}>
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton size="small" color="error" onClick={() => handleRemove(ref.id)}>
                  <DeleteOutlineIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}

        {/* Add reference form */}
        {showForm && (
          <Box
            sx={{
              p: 1.5,
              backgroundColor: "white",
              border: "1px solid #bdbdbd",
              borderRadius: 1,
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              New Reference
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel>Source type</InputLabel>
              <Select
                label="Source type"
                value={form.refType}
                onChange={(e) => setForm({ ...form, refType: e.target.value })}
              >
                <MenuItem value="journal">Journal Article</MenuItem>
                <MenuItem value="book">Book</MenuItem>
                <MenuItem value="website">Website</MenuItem>
                <MenuItem value="conference">Conference Paper</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Authors"
              placeholder="Smith, J., Jones, A."
              value={form.authors}
              onChange={(e) => setForm({ ...form, authors: e.target.value })}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Year"
              placeholder="2024"
              value={form.year}
              onChange={(e) => setForm({ ...form, year: e.target.value })}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Journal / Publisher / Site"
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              size="small"
              label="URL or DOI"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="contained" size="small" onClick={handleAdd} fullWidth>
                Add
              </Button>
              <Button
                size="small"
                fullWidth
                onClick={() => {
                  setShowForm(false);
                  setForm(BLANK_REF);
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <Divider />
      {!showForm && (
        <Box sx={{ p: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            startIcon={<AddIcon />}
            onClick={() => setShowForm(true)}
          >
            Add Reference
          </Button>
        </Box>
      )}
    </Box>
  );
}
